import { useEffect, useMemo, useRef, useState } from "react";
import { FieldRow } from "@/components/ui/Field";
import { formatAuftragLookupLabel } from "@/features/globalUpload/formatAuftragLookupLabel";
import { searchOrders } from "@/lib/projectOverview";
import type { AuftragLookup } from "@/lib/material";
import type { PlanungOrderLink } from "@/lib/montageplanung";

function toAuftragLookup(o: {
  id: number;
  auftragsnummer?: string;
  kunde?: string;
  beschreibung?: string;
  jahr?: number;
  ort?: string;
}): AuftragLookup {
  return {
    id: o.id,
    auftragsnummer: o.auftragsnummer?.trim() || String(o.id),
    jahr: o.jahr ?? null,
    kunde: o.kunde ?? null,
    ort: o.ort ?? null,
    beschreibung: o.beschreibung ?? null,
    ort_zusatz: null,
  };
}

export function AssignmentOrdersField({
  orders,
  onChange,
  label = "Projekte",
  hint = "Nach Auftragsnummer, Ort, Kunde oder Beschreibung suchen. Mehrere Projekte möglich.",
  onFirstAdded,
}: {
  orders: PlanungOrderLink[];
  onChange: (next: PlanungOrderLink[]) => void;
  label?: string;
  hint?: string;
  onFirstAdded?: (order: AuftragLookup) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<AuftragLookup[]>([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef<number | null>(null);
  const selectedIds = useMemo(() => new Set(orders.map((o) => o.order_id)), [orders]);

  useEffect(() => {
    const q = query.trim();
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimer.current = window.setTimeout(async () => {
      try {
        const rows = await searchOrders(q, 8);
        setResults(rows.map(toAuftragLookup));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => {
      if (searchTimer.current) window.clearTimeout(searchTimer.current);
    };
  }, [query]);

  function addOrder(order: AuftragLookup) {
    if (selectedIds.has(order.id)) return;
    const next = [...orders, { order_id: order.id, order_label: formatAuftragLookupLabel(order) }];
    onChange(next);
    if (orders.length === 0) onFirstAdded?.(order);
    setQuery("");
    setResults([]);
  }

  return (
    <FieldRow label={label} hint={hint}>
      {orders.length > 0 ? (
        <ul className="mb-2 space-y-1">
          {orders.map((o) => (
            <li
              key={o.order_id}
              className="flex items-center justify-between gap-2 rounded-lg border border-separator-opaque px-3 py-2 text-sm"
            >
              <span className="min-w-0 line-clamp-2 break-words">
                {o.order_label || `Auftrag ${o.order_id}`}
              </span>
              <button
                type="button"
                className="shrink-0 text-status-red hover:underline"
                onClick={() => onChange(orders.filter((item) => item.order_id !== o.order_id))}
              >
                Entfernen
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="relative">
        <input
          className="eh-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Projekt suchen und hinzufügen…"
        />
        {(searching || results.length > 0) && query.trim().length >= 2 ? (
          <div className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-separator-opaque bg-card shadow-ios-md">
            {searching ? (
              <div className="px-3 py-2 text-sm text-label-secondary">Suche…</div>
            ) : null}
            {!searching &&
              results
                .filter((o) => !selectedIds.has(o.id))
                .map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    className="block w-full px-3 py-2 text-left text-sm line-clamp-2 break-words hover:bg-fill-tertiary"
                    onClick={() => addOrder(o)}
                  >
                    {formatAuftragLookupLabel(o)}
                  </button>
                ))}
            {!searching && results.filter((o) => !selectedIds.has(o.id)).length === 0 ? (
              <div className="px-3 py-2 text-sm text-label-secondary">Keine Treffer</div>
            ) : null}
          </div>
        ) : null}
      </div>
    </FieldRow>
  );
}
